import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {EChartsOption} from 'echarts';
import {ScoreDevelopmentChipVm, ScoreDevelopmentVm} from '../../models/team-dynamic.vm';

interface ScoreDevelopmentChipView {
  chip: ScoreDevelopmentChipVm;
  active: boolean;
  buttonStyle: { [key: string]: string };
  iconStyle: { [key: string]: string };
}

@Component({
  selector: 'app-score-development',
  templateUrl: './score-development.component.html',
  styleUrls: ['./score-development.component.scss']
})
export class ScoreDevelopmentComponent implements OnChanges {
  @Input() scores: ScoreDevelopmentVm = {labels: [], values: []};
  @Input() chips: ScoreDevelopmentChipVm[] = [];
  @Input() activeChipUuid: string | null = null;
  @Input() loading: boolean = false;
  @Input() showChips: boolean = true;
  @Input() titleKey: string = 'td_score_development';
  @Input() comparisonScores: ScoreDevelopmentVm | null = null;
  @Input() comparisonTitle: string = '';
  @Input() comparisonColor: string = '#AEB9D6';

  @Output() chipSelected = new EventEmitter<string | null>();

  hasData: boolean = false;
  showChart: boolean = true;
  activeChipTitle: string = '';
  chipViews: ScoreDevelopmentChipView[] = [];
  private seriesColors: string[] = [];

  chartOption: EChartsOption = {
    grid: {
      left: 4,
      right: 16,
      top: 34,
      bottom: 4,
      containLabel: true
    },
    xAxis: {
      type: 'category',
      boundaryGap: true,
      axisLabel: {
        color: '#8B93AB',
        fontWeight: 'bold',
        fontSize: 10
      },
      axisLine: {
        show: false
      },
      axisTick: {
        show: false
      },
      data: []
    },
    yAxis: {
      type: 'value',
      min: 0,
      max: 100,
      interval: 25,
      axisLabel: {
        color: '#8B93AB',
        fontWeight: 'bold',
        fontSize: 10,
        formatter: (value: number) => (value % 50 === 0 ? '' + value : '')
      },
      axisLine: {
        show: false
      },
      axisTick: {
        show: false
      },
      splitLine: {
        lineStyle: {
          color: '#E9EDF6'
        }
      }
    },
    tooltip: {
      trigger: 'axis',
      padding: [6, 10],
      textStyle: {
        fontSize: 11,
        color: '#16223F'
      },
      formatter: (params: any) => {
        const points = Array.isArray(params) ? params : [params];
        const rows = points.map((point: any) =>
          '<span style="display:inline-block;width:8px;height:8px;border-radius:50%;'
          + 'margin-right:6px;background-color:'
          + (this.seriesColors[point.seriesIndex] ?? point.color) + '"></span>'
          + point.seriesName + ': <b>' + point.value + '%</b>');
        return points[0].name + '<br/>' + rows.join('<br/>');
      }
    },
    series: [
      {
        name: '',
        type: 'line',
        data: [],
        smooth: false,
        symbol: 'circle',
        symbolSize: 9,
        label: {
          show: true,
          position: 'top',
          distance: 8,
          color: '#16223F',
          fontWeight: 'bold',
          fontSize: 11,
          formatter: '{c}%'
        },
        tooltip: {
          trigger: 'axis'
        }
      }
    ]
  };

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['chips'] || changes['activeChipUuid']) {
      this.buildChipViews();
    }
    if (changes['scores'] || changes['activeChipUuid'] || changes['chips'] || changes['comparisonScores']) {
      this.fillChart();
    }
  }

  selectChip(chip: ScoreDevelopmentChipVm) {
    this.chipSelected.emit(chip.uuid);
  }

  trackByUuid(index: number, view: ScoreDevelopmentChipView) {
    return view.chip.uuid ?? index;
  }

  private buildChipViews() {
    this.chipViews = this.chips.map(chip => {
      const active = chip.uuid === this.activeChipUuid;
      const buttonStyle: { [key: string]: string } = active
        ? {'background-color': chip.color, 'border-color': chip.color}
        : {};
      return {
        chip,
        active,
        buttonStyle,
        iconStyle: {
          '-webkit-mask-image': 'url(' + chip.icon + ')',
          'mask-image': 'url(' + chip.icon + ')',
          'background-color': active ? '#FFFFFF' : chip.color
        }
      };
    });
  }

  private fillChart() {
    const labels = this.scores?.labels ?? [];
    const values = this.scores?.values ?? [];
    const comparisonLabels = this.comparisonScores?.labels ?? [];
    const comparisonValues = this.comparisonScores?.values ?? [];
    const activeChip = this.chips.find(chip => chip.uuid === this.activeChipUuid);
    const color = activeChip?.color ?? '#F2762E';
    const axisLabels = labels.length ? labels : comparisonLabels;

    this.hasData = values.length > 0 || comparisonValues.length > 0;
    this.activeChipTitle = activeChip?.title ?? '';

    // @ts-ignore
    const series = this.chartOption.series[0];
    series.name = this.activeChipTitle;
    series.data = [];
    series.color = color;
    series.lineStyle = {color, width: 2};
    series.itemStyle = {color: '#FFFFFF', borderColor: color, borderWidth: 2};
    // @ts-ignore
    this.chartOption.xAxis.data = [];

    const valuesByLabel = this.mapValuesByLabel(labels, values);
    axisLabels.forEach((label: string) => {
      // @ts-ignore
      this.chartOption.xAxis.data.push(label);
      series.data.push({value: valuesByLabel[label] ?? null, name: label});
    });

    this.chartOption.series = comparisonValues.length
      ? [series, this.buildComparisonSeries(axisLabels, comparisonLabels, comparisonValues)]
      : [series];
    this.seriesColors = comparisonValues.length ? [color, this.comparisonColor] : [color];

    this.showChart = !this.showChart;
  }

  private buildComparisonSeries(axisLabels: string[], labels: string[], values: number[]): any {
    const valuesByLabel = this.mapValuesByLabel(labels, values);
    const color = this.comparisonColor;

    return {
      name: this.comparisonTitle,
      type: 'line',
      z: 3,
      data: axisLabels.map((label: string) => ({value: valuesByLabel[label] ?? null, name: label})),
      smooth: false,
      symbol: 'circle',
      symbolSize: 9,
      color,
      lineStyle: {color, width: 2},
      itemStyle: {color: '#FFFFFF', borderColor: color, borderWidth: 2},
      label: {show: false},
      tooltip: {trigger: 'axis'}
    };
  }

  private mapValuesByLabel(labels: string[], values: number[]): { [key: string]: number } {
    return labels.reduce((acc: any, label: string, index: number) => {
      acc[label] = values[index];
      return acc;
    }, {});
  }
}
